import React, { useState, useEffect } from "react";
import { UserPlus2, LogIn, User, Mail, Phone, Building2, MapPin, Lock } from "lucide-react";
import "../css/popularCTA.css";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom"; 

const PopularCTA = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(false); 
  const [role, setRole] = useState("");
  const navigate = useNavigate(); 
  
  const initialDynamicProfile = {
    studentFields: { currentQualification: "", interestedCourse: "", preferredCountry: "", budgetRange: "", studyTimeline: "" },
    principalFields: { schoolName: "", board: "", totalStudents: "" },
    teacherFields: { currentOrganization: "", subjectTaught: "", yearsOfExperience: "", preferredLocation: "" },
    counsellorFields: { organizationName: "", yearsOfExperience: "", studentVolumeMonthly: "" },
    coachingFields: { instituteName: "", coursesOffered: "", numberOfStudents: "" }
  };

  useEffect(() => {
    const mode = searchParams.get("mode");
    setIsLogin(mode === "login");
    
    setRole("");
    setStatusMsg("");
    setFormData({ fullName: "", email: "", phoneNumber: "", city: "", state: "", message: "", password: "" });
    setDynamicProfile(initialDynamicProfile);
  }, [searchParams]);

  const [formData, setFormData] = useState({
    fullName: "", email: "", phoneNumber: "", city: "", state: "", message: "", password: "" 
  });

  const [dynamicProfile, setDynamicProfile] = useState(initialDynamicProfile);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (e) => {
    const selectedRole = e.target.value;
    setRole(selectedRole);
    setFormData(prev => ({ ...prev, password: "" }));
    setStatusMsg("");
    
    // Reset to default on role change to ensure fresh object data
    setDynamicProfile({
      studentFields: { currentQualification: "", interestedCourse: "", preferredCountry: "", budgetRange: "", studyTimeline: "" },
      principalFields: { schoolName: "", board: "", totalStudents: "" },
      teacherFields: { currentOrganization: "", subjectTaught: "", yearsOfExperience: "", preferredLocation: "" },
      counsellorFields: { organizationName: "", yearsOfExperience: "", studentVolumeMonthly: "" },
      coachingFields: { instituteName: "", coursesOffered: "", numberOfStudents: "" }
    });
  };

  const handleDynamicChange = (section, field, value) => {
    setDynamicProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleFormMode = () => {
    setIsLogin(!isLogin);
    setRole("");
    setStatusMsg("");
    setFormData({ fullName: "", email: "", phoneNumber: "", city: "", state: "", message: "", password: "" });
    setDynamicProfile(initialDynamicProfile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) {
      setStatusMsg("Please select a role first.");
      return;
    }
    setLoading(true);
    setStatusMsg("");

    if (isLogin && role === "Admin") {
      setLoading(false);
      if (formData.email === "admin@evridya.com" && formData.password === "Admin@123") {
        localStorage.setItem("token", "admin-mock-token-eVridya");
        localStorage.setItem("role", "admin");
        navigate("/admin");
        window.location.reload();
      } else {
        setStatusMsg("Error! Invalid Admin Credentials.");
      }
      return;
    }

    // Convert keys to database compliant ENUM values
    let dbRole = role.toLowerCase().trim();
    if (dbRole === "career-counsellor") dbRole = "career counsellor";
    if (dbRole === "coaching-partner") dbRole = "coaching partner";

    const endpoint = isLogin ? "https://api.satrixstore.in/api/login" : "https://api.satrixstore.in/api/signup";
    
    // Explicit payload configuration to eliminate undefined data objects
    const payload = isLogin 
      ? { role: dbRole, email: formData.email } 
      : { 
          role: dbRole, 
          ...formData, 
          password: formData.password || "DefaultPassword123", 
          dynamicProfile: {
            studentFields: dynamicProfile.studentFields,
            principalFields: dynamicProfile.principalFields,
            teacherFields: dynamicProfile.teacherFields,
            counsellorFields: dynamicProfile.counsellorFields, // Assured integration
            coachingFields: dynamicProfile.coachingFields
          }
        };

    try {
      const response = await axios.post(endpoint, payload);
      
      if (isLogin && response.data) {
        const userToken = response.data.token || "mock-user-token";
        const userRole = (response.data.role || dbRole).toLowerCase().trim();
        const userData = response.data.user || response.data;

        localStorage.setItem("token", userToken);
        localStorage.setItem("role", userRole);
        localStorage.setItem("user", JSON.stringify(userData)); 
        localStorage.setItem("fullName", userData.fullName || "User");

        const roleRoutes = {
          "admin": "/admin",
          "student": "/student/me", 
          "principal": "/principal-dashboard",
          "teacher": "/teacher-dashboard",
          "career counsellor": `/admin/career-counsellor/${userData.id || 'me'}`,
          "coaching partner": "/coachingpartner"
        };

        navigate(roleRoutes[userRole] || "/", { state: { user: userData } });
        window.location.reload();
      } else {
        setStatusMsg("Success! Registered Successfully. Please Login.");
        toggleFormMode();
      }
    } catch (error) {
      if (error.response) {
        setStatusMsg(error.response.data?.message || "Error! Authentication failed.");
      } else if (error.request) {
        setStatusMsg("Error! Server is not responding. Please check your connection.");
      } else {
        setStatusMsg("Error! " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="cta-section">
      <div className="cta-container">
        {/* LEFT CONTENT */}
        <div className="cta-left">
          <h2 className="cta-heading">
            Start Your Global <br />
            <span className="highlight-green">Journey Today</span>
          </h2>
          <p className="cta-desc">
            Get expert guidance for study abroad, career counselling, and university admissions. 
            We help you choose the right path to a successful future.
          </p>

          <div className="cta-features">
            <div className="feature-item">
              <div className="icon-box green">🎓</div>
              <div className="feature-text">
                <strong>Personalized Career Counselling</strong>
                <span>Expert advice tailored to your goals</span>
              </div>
            </div>
            <div className="feature-item">
              <div className="icon-box blue">🌐</div>
              <div className="feature-text">
                <strong>Study Abroad Guidance</strong>
                <span>Find the best universities worldwide</span>
              </div>
            </div>
            <div className="feature-item">
              <div className="icon-box blue">🏛️</div>
              <div className="feature-text">
                <strong>University Admission Support</strong>
                <span>End-to-end application assistance</span>
              </div>
            </div>
            <div className="feature-item">
              <div className="icon-box blue">📅</div>
              <div className="feature-text">
                <strong>Workshops & Events</strong>
                <span>Stay updated and ahead of the curve</span>
              </div>
            </div>
          </div>

          <div className="help-banner">
             <div className="phone-icon-bg">📞</div>
             <div className="help-info">
                <span className="help-label">Need Help?</span>
                <span className="help-num">+91 8287-543-429</span>
             </div>
          </div>
        </div>

        {/* RIGHT FORM CARD */}
        <form onSubmit={handleSubmit} className="cta-form-card">
          <div className="form-header">
            <div className="user-circle">
              {isLogin ? <LogIn size={40} color="#3b82f6" /> : <UserPlus2 size={40} color="#3b82f6" />}
            </div>
            <h3>{isLogin ? "Login Account" : "Register With Us"}</h3>
          </div>
          
          {statusMsg && (
            <div className="status-banner" style={{padding: '10px', margin: '10px 0', borderRadius: '5px', background: statusMsg.includes('Success') ? '#dcfce7' : '#fee2e2', color: statusMsg.includes('Success') ? '#15803d' : '#b91c1c'}}>
              {statusMsg}
            </div>
          )}

          <div className="form-grid">
            {/* ROLE SELECT */}
            <div className="input-group">
              <select onChange={handleRoleChange} value={role} className="role-select" required>
                <option value="">Select Role</option>
                <option value="Student">Student</option>
                <option value="Principal">Principal</option>
                <option value="Teacher">Teacher</option>
                <option value="Career-Counsellor">Career Counsellor</option>
                <option value="Coaching-Partner">Coaching Partner</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            {isLogin ? (
              <>
                <div className="input-group">
                  <Mail className="field-icon" size={18} />
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" required />
                </div>
                {role === "Admin" && (
                  <div className="input-group">
                    <Lock className="field-icon" size={18} />
                    <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Admin Password" required />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="input-group">
                  <User className="field-icon" size={18} />
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Full Name" required />
                </div>

                <div className="input-group">
                  <Mail className="field-icon" size={18} />
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" required />
                </div>

                <div className="input-group">
                  <Phone className="field-icon" size={18} />
                  <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder="Phone Number" required />
                </div>
                
                <div className="form-row">
                  <div className="input-group">
                    <Building2 className="field-icon" size={18} />
                    <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" />
                  </div>
                  <div className="input-group">
                    <MapPin className="field-icon" size={18} />
                    <input type="text" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" />
                  </div>
                </div>

                {/* DYNAMIC FIELDS */}
                <div className={`dynamic-fields ${role ? "show" : ""}`}>
                  {role === "Student" && (
                    <div className="role-fields-container">
                      <input type="text" placeholder="Current Class / Qualification" className="dynamic-input" value={dynamicProfile.studentFields.currentQualification} onChange={(e) => handleDynamicChange("studentFields", "currentQualification", e.target.value)} />
                      <input type="text" placeholder="Interested Course" className="dynamic-input" value={dynamicProfile.studentFields.interestedCourse} onChange={(e) => handleDynamicChange("studentFields", "interestedCourse", e.target.value)} />
                      <input type="text" placeholder="Preferred Country" className="dynamic-input" value={dynamicProfile.studentFields.preferredCountry} onChange={(e) => handleDynamicChange("studentFields", "preferredCountry", e.target.value)} />
                      <div className="form-row">
                        <input type="text" placeholder="Budget Range" className="dynamic-input" value={dynamicProfile.studentFields.budgetRange} onChange={(e) => handleDynamicChange("studentFields", "budgetRange", e.target.value)} />
                        <input type="text" placeholder="Study Timeline" className="dynamic-input" value={dynamicProfile.studentFields.studyTimeline} onChange={(e) => handleDynamicChange("studentFields", "studyTimeline", e.target.value)} />
                      </div>
                    </div>
                  )}

                  {role === "Principal" && (
                    <div className="role-fields-container">
                      <input type="text" placeholder="School Name" className="dynamic-input" value={dynamicProfile.principalFields.schoolName} onChange={(e) => handleDynamicChange("principalFields", "schoolName", e.target.value)} />
                      <select className="dynamic-input" value={dynamicProfile.principalFields.board} onChange={(e) => handleDynamicChange("principalFields", "board", e.target.value)}>
                        <option value="">Select Board</option>
                        <option value="CBSE">CBSE</option>
                        <option value="ICSE">ICSE</option>
                        <option value="IB">IB</option>
                        <option value="IGCSE">IGCSE</option>
                        <option value="State Board">State Board</option>
                      </select>
                      <input type="number" placeholder="Total Students" className="dynamic-input" value={dynamicProfile.principalFields.totalStudents} onChange={(e) => handleDynamicChange("principalFields", "totalStudents", e.target.value)} />
                    </div>
                  )}

                  {role === "Teacher" && (
                    <div className="role-fields-container">
                      <input type="text" placeholder="Current School / Organization" className="dynamic-input" value={dynamicProfile.teacherFields.currentOrganization} onChange={(e) => handleDynamicChange("teacherFields", "currentOrganization", e.target.value)} />
                      <input type="text" placeholder="Subject Taught" className="dynamic-input" value={dynamicProfile.teacherFields.subjectTaught} onChange={(e) => handleDynamicChange("teacherFields", "subjectTaught", e.target.value)} />
                      <div className="form-row">
                        <input type="text" placeholder="Years of Experience" className="dynamic-input" value={dynamicProfile.teacherFields.yearsOfExperience} onChange={(e) => handleDynamicChange("teacherFields", "yearsOfExperience", e.target.value)} />
                        <input type="text" placeholder="Preferred Location" className="dynamic-input" value={dynamicProfile.teacherFields.preferredLocation} onChange={(e) => handleDynamicChange("teacherFields", "preferredLocation", e.target.value)} />
                      </div>
                    </div>
                  )}

                  {role === "Career-Counsellor" && (
                    <div className="role-fields-container">
                      <input type="text" placeholder="Organization Name" className="dynamic-input" value={dynamicProfile.counsellorFields.organizationName} onChange={(e) => handleDynamicChange("counsellorFields", "organizationName", e.target.value)} />
                      <input type="text" placeholder="Years of Experience" className="dynamic-input" value={dynamicProfile.counsellorFields.yearsOfExperience} onChange={(e) => handleDynamicChange("counsellorFields", "yearsOfExperience", e.target.value)} />
                      <input type="number" placeholder="Student Volume (Monthly)" className="dynamic-input" value={dynamicProfile.counsellorFields.studentVolumeMonthly} onChange={(e) => handleDynamicChange("counsellorFields", "studentVolumeMonthly", e.target.value)} />
                    </div>
                  )}

                  {role === "Coaching-Partner" && (
                    <div className="role-fields-container">
                      <input type="text" placeholder="Institute Name" className="dynamic-input" value={dynamicProfile.coachingFields.instituteName} onChange={(e) => handleDynamicChange("coachingFields", "instituteName", e.target.value)} />
                      <select className="dynamic-input" value={dynamicProfile.coachingFields.coursesOffered} onChange={(e) => handleDynamicChange("coachingFields", "coursesOffered", e.target.value)}>
                        <option value="">Courses Offered</option>
                        <option value="NIFT / NIT">NIFT / NIT</option>
                        <option value="JEE / NEET">JEE / NEET</option>
                      </select>
                      <input type="number" placeholder="Number of Students" className="dynamic-input" value={dynamicProfile.coachingFields.numberOfStudents} onChange={(e) => handleDynamicChange("coachingFields", "numberOfStudents", e.target.value)} />
                    </div>
                  )}
                </div>

                <div className="input-group">
                  <textarea className="form-textarea" name="message" value={formData.message} onChange={handleInputChange} placeholder="Message / Requirement"></textarea>
                </div>
              </>
            )}

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (isLogin ? "Logging in..." : "Registering...") : (isLogin ? "Login Now →" : "Register Now →")}
            </button>
            
            <div className="form-footer-note" style={{ display: 'flex', justifyContent: 'center', marginTop: '15px', cursor: 'pointer' }} onClick={toggleFormMode}>
              <span style={{ color: '#4b5563', fontSize: '14px' }}>
                {isLogin ? (
                  <>Don't have an account? <strong style={{ color: '#3b82f6', textDecoration: 'underline' }}>Register here</strong></>
                ) : (
                  <>Already have an account? <strong style={{ color: '#3b82f6', textDecoration: 'underline' }}>Login here</strong></>
                )}
              </span>
            </div>

          </div>
        </form>
      </div>
    </section>
  );
};

export default PopularCTA;